/**
 * E2E testing contract (LainGram).
 *
 * Single source of truth for the `data-testid` markers Playwright attaches
 * to. Rules:
 *  - markers are ALWAYS compiled in (inert attributes, zero runtime cost) so
 *    the production bundle stays testable;
 *  - names are hierarchical `namespace:element`, kebab-case;
 *  - many elements can share one testid (e.g. every dialog row) — combine
 *    with the element's own data attributes (`data-peer-id`, `data-mid`) for
 *    specificity;
 *  - add a marker here BEFORE using it in a component, never inline string
 *    literals in the DOM code.
 */
export const E2E_TEST_IDS = {
  // auth flow
  authCard: 'auth:card',
  authPhoneInput: 'auth:phone-input',
  authCountry: 'auth:country',
  authNext: 'auth:next',
  authLoginByPhone: 'auth:login-by-phone',
  authLoginQr: 'auth:login-qr',
  authCodeCard: 'auth:code-card',
  authCodeInput: 'auth:code-input',
  authSignUpCard: 'auth:sign-up-card',
  authNameInput: 'auth:name-input',
  authStart: 'auth:start',
  // dialogs list (chat list sidebar)
  dialogRow: 'dialog:row',
  dialogsList: 'dialog:list',
  // left sidebar
  sidebarMenuButton: 'sidebar:menu-button',
  sidebarBackButton: 'sidebar:back-button',
  menuNewGroup: 'sidebar:menu-new-group',
  sidebarContacts: 'sidebar:contacts',
  // new group flow
  newGroupSearch: 'new-group:peer-search',
  newGroupPeerList: 'new-group:peer-list',
  newGroupMembersNext: 'new-group:members-next',
  newGroupTitle: 'new-group:title',
  newGroupCreate: 'new-group:create',
  // contacts
  addContactButton: 'contacts:add-contact',
  contactFirstName: 'contacts:first-name',
  contactLastName: 'contacts:last-name',
  contactPhone: 'contacts:phone',
  contactSave: 'contacts:save',
  // chat view
  chatComposerInput: 'chat:composer-input'
} as const;

export type E2eTestId = typeof E2E_TEST_IDS[keyof typeof E2E_TEST_IDS];

/** Set the marker on a DOM element (dataset camelCase → data-testid). */
export function setE2eTestId(element: HTMLElement | SVGElement, id: E2eTestId) {
  (element as HTMLElement).dataset.testid = id;
}
